import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, imgSrc } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import Lightbox from "@/components/Lightbox";
import { ReviewForm, Stars } from "@/components/Reviews";
import { ArrowUpRight, Check, Expand } from "lucide-react";

export default function CabinDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { t } = useT();
    const { format } = useCurrency();
    const [cabin, setCabin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reviewData, setReviewData] = useState({ count: 0, average: null, reviews: [] });
    const [lightboxIdx, setLightboxIdx] = useState(null);

    useEffect(() => {
        let active = true;
        setLoading(true);
        api.get(`/cabins/${slug}`)
            .then((r) => { if (active) setCabin(r.data); })
            .catch(() => { if (active) setCabin(null); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [slug]);

    useEffect(() => {
        if (!cabin?.id) return;
        api.get(`/cabins/${cabin.id}/reviews`).then((r) => setReviewData(r.data)).catch(() => {});
    }, [cabin?.id]);

    const refreshReviews = () => {
        if (cabin?.id) api.get(`/cabins/${cabin.id}/reviews`).then((r) => setReviewData(r.data));
    };

    if (loading) {
        return (
            <div className="bg-bone min-h-screen">
                <Navbar />
                <div className="pt-40 px-6 lg:px-12 font-serif text-2xl text-espresso">Loading…</div>
            </div>
        );
    }
    if (!cabin) {
        return (
            <div className="bg-bone min-h-screen">
                <Navbar />
                <div className="pt-40 px-6 lg:px-12 text-center" data-testid="cabin-not-found">
                    <h1 className="font-chiller text-4xl mb-4">Cabin not found</h1>
                    <Link to="/cabins" className="text-forest underline">Back to cabins</Link>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="cabin-detail-page" className="bg-bone min-h-screen">
            <Navbar />
            {/* Hero image */}
            <div className="pt-20">
                <button
                    onClick={() => setLightboxIdx(0)}
                    data-testid="cabin-hero-image"
                    className="w-full aspect-[16/8] overflow-hidden block relative group"
                >
                    <img src={imgSrc(cabin.images?.[0])} alt={cabin.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.02]" />
                    <span className="absolute bottom-6 right-6 inline-flex items-center gap-2 px-4 py-2 bg-forestDark/70 text-bone text-xs uppercase tracking-widest2 backdrop-blur-sm">
                        <Expand size={14} /> View gallery
                    </span>
                </button>
            </div>

            <div className="px-6 lg:px-12 py-16 lg:py-24">
                <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-4 mb-4 flex-wrap">
                            <div className="eyebrow">Cabin</div>
                            {reviewData.average !== null && (
                                <div className="flex items-center gap-2 text-sm text-espresso" data-testid="cabin-rating-summary">
                                    <Stars value={reviewData.average} />
                                    <span>{reviewData.average} · {reviewData.count} review{reviewData.count !== 1 ? "s" : ""}</span>
                                </div>
                            )}
                        </div>
                        <h1 className="font-chiller text-5xl lg:text-7xl leading-[1.02] tracking-tight mb-4">{cabin.name}</h1>
                        <p className="text-lg text-muted2 leading-relaxed max-w-2xl mb-8">{cabin.tagline}</p>
                        <div className="hairline mb-8" />
                        <div className="prose max-w-none text-base text-ink/80 leading-relaxed mb-12">
                            <p>{cabin.description}</p>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
                            <Spec label="Guests" value={cabin.max_guests} />
                            <Spec label="Bedrooms" value={cabin.bedrooms} />
                            <Spec label="Bathrooms" value={cabin.bathrooms} />
                            <Spec label="Size" value={`${cabin.size_sqft} sqft`} />
                        </div>

                        <h2 className="font-chiller text-3xl mb-6">In the cabin</h2>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-12">
                            {cabin.amenities?.map((a) => (
                                <li key={a} className="flex items-center gap-3 text-sm text-ink/80"><Check size={14} className="text-terracotta" /> {a}</li>
                            ))}
                        </ul>

                        {cabin.images?.length > 1 && (
                            <>
                                <h2 className="font-chiller text-3xl mb-6">{t("cabinDetail.gallery")}</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
                                    {cabin.images.map((img, idx) => (
                                        <button
                                            key={img}
                                            onClick={() => setLightboxIdx(idx)}
                                            data-testid={`cabin-gallery-${idx}`}
                                            className="aspect-[4/3] overflow-hidden bg-bone2 block group relative"
                                        >
                                            <img src={imgSrc(img)} alt={`${cabin.name} ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                            <span className="absolute bottom-3 right-3 p-2 bg-forestDark/60 text-bone opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Expand size={14} />
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Reviews */}
                        <section className="mt-16" data-testid="cabin-reviews-section">
                            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                                <div>
                                    <div className="eyebrow mb-2">Guest reviews</div>
                                    <h2 className="font-chiller text-4xl lg:text-5xl">What guests are saying.</h2>
                                </div>
                                {reviewData.average !== null && (
                                    <div className="text-right">
                                        <div className="font-serif text-5xl text-forest">{reviewData.average}</div>
                                        <Stars value={reviewData.average} size={16} />
                                        <div className="text-xs text-muted2 mt-1">{reviewData.count} review{reviewData.count !== 1 ? "s" : ""}</div>
                                    </div>
                                )}
                            </div>

                            {reviewData.reviews.length === 0 ? (
                                <p className="text-muted2 text-sm mb-10" data-testid="reviews-empty">No reviews yet — be the first to share your stay.</p>
                            ) : (
                                <div className="space-y-8 mb-12">
                                    {reviewData.reviews.map((r) => (
                                        <article key={r.id} className="border-l-2 border-terracotta pl-6 py-2" data-testid={`review-${r.id}`}>
                                            <div className="flex items-center gap-3 mb-2">
                                                <Stars value={r.rating} />
                                                <span className="text-xs uppercase tracking-widest2 text-espresso/70">{r.guest_name}</span>
                                                <span className="text-xs text-muted2">· {String(r.created_at).slice(0, 10)}</span>
                                            </div>
                                            {r.title && <h4 className="font-serif text-2xl mb-2">{r.title}</h4>}
                                            <p className="text-base text-ink/80 leading-relaxed">{r.body}</p>
                                        </article>
                                    ))}
                                </div>
                            )}

                            <ReviewForm cabinId={cabin.id} onSubmitted={refreshReviews} />
                        </section>
                    </div>

                    {/* Booking card */}
                    <aside className="lg:col-span-4 lg:col-start-9">
                        <div className="border border-espresso/15 bg-bone p-8 lg:sticky lg:top-32" data-testid="cabin-booking-card">
                            <div className="eyebrow mb-2">{t("common.from")}</div>
                            <div className="font-serif text-4xl mb-1">{format(cabin.price_per_night)}<span className="text-base text-muted2"> {t("common.perNight")}</span></div>
                            {(cabin.weekend_premium_pct > 0 || (cabin.seasons && cabin.seasons.length > 0)) && (
                                <div className="mt-3 flex flex-wrap gap-2" data-testid="cabin-rate-badges">
                                    {cabin.weekend_premium_pct > 0 && (
                                        <span className="text-[10px] uppercase tracking-widest2 px-2 py-1 bg-forest/10 text-forest">
                                            {t("cabinDetail.weekendPremiumBadge", cabin.weekend_premium_pct)}
                                        </span>
                                    )}
                                    {cabin.seasons && cabin.seasons.length > 0 && (
                                        <span className="text-[10px] uppercase tracking-widest2 px-2 py-1 bg-terracotta/10 text-terracotta">
                                            {t("cabinDetail.hasSeasons")}
                                        </span>
                                    )}
                                </div>
                            )}
                            {reviewData.average !== null && (
                                <div className="mt-2 flex items-center gap-2 text-xs text-espresso">
                                    <Stars value={reviewData.average} size={12} />
                                    <span>{reviewData.average} · {reviewData.count} reviews</span>
                                </div>
                            )}
                            <div className="hairline my-6" />
                            <ul className="text-sm text-muted2 space-y-3 mb-8" data-testid="cabin-stay-info">
                                <li className="flex justify-between"><span>Max guests</span><span className="text-ink">{cabin.max_guests}</span></li>
                                <li className="flex justify-between"><span>Min stay</span><span className="text-ink">3 nights</span></li>
                                <li className="flex justify-between"><span>Check-in</span><span className="text-ink">3:00 PM</span></li>
                                <li className="flex justify-between"><span>Check-out</span><span className="text-ink">11:00 AM</span></li>
                                <li className="flex justify-between"><span>Guests</span><span className="text-ink">Adults only</span></li>
                            </ul>
                            <button
                                onClick={() => navigate(`/book?cabin=${cabin.id}`)}
                                data-testid="cabin-reserve-btn"
                                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-forest text-bone hover:bg-forestDark transition-colors text-xs uppercase tracking-widest2 font-semibold"
                            >
                                {t("cabinDetail.reserveCabin")} <ArrowUpRight size={16} />
                            </button>
                            <p className="mt-4 text-xs text-muted2 leading-relaxed">{t("cabinDetail.confirmNote")}</p>
                        </div>
                    </aside>
                </div>
            </div>

            <Lightbox
                images={(cabin.images || []).map(imgSrc)}
                openIndex={lightboxIdx}
                onClose={() => setLightboxIdx(null)}
            />

            <Footer />
        </div>
    );
}

function Spec({ label, value }) {
    return (
        <div className="border-l border-espresso/20 pl-4">
            <div className="eyebrow text-espresso/70 mb-1">{label}</div>
            <div className="font-serif text-2xl text-ink">{value}</div>
        </div>
    );
}
